import { Box, ThemeProvider, createTheme } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PlayerProvider } from './contexts/PlayerContext';
import { UserPreferencesProvider, useUserPreferences } from './contexts/UserPreferencesContext';
import { PlanProvider, usePlan } from './contexts/PlanContext';
import { createAppTheme } from './theme';
import Login from './pages/Login';
import PrivateRoute from './components/PrivateRoute';
import { useState, useEffect } from 'react';
import React from 'react';
import { CssBaseline } from '@mui/material';
import Navigation from './components/Navigation';
import { useAuth } from './contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './services/firebase';
import Meditations from './components/Meditations';
import Events from './components/Events';
import MiniPlayer from './components/MiniPlayer';
import PageLayout from './components/PageLayout';
import BookRecommendations from './components/BookRecommendations';

// Componente interno que usa o useAuth
function AppContent() {
  const { currentUser } = useAuth();
  const { preferences, updatePreferences } = useUserPreferences();
  const { mode, customColors } = preferences;
  const [currentTab, setCurrentTab] = useState('menu');
  const [habits, setHabits] = useState([]);
  const [showDailyQuote, setShowDailyQuote] = useState(true);
  const { currentPlan, PLANS } = usePlan();

  const theme = createTheme({
    palette: {
      mode: mode,
      primary: {
        main: customColors?.primary || '#1B365D',
      },
      secondary: {
        main: customColors?.secondary || '#78B6CB',
      },
    },
  });

  // Load user habits
  React.useEffect(() => {
    const loadUserHabits = async () => {
      if (currentUser?.uid) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setHabits(userData.habits || []);
          }
        } catch (error) {
          console.error('Erro ao carregar hábitos:', error);
        }
      }
    };
    loadUserHabits();
  }, [currentUser]);

  const handleUpdateCustomColors = (newColors) => {
    updatePreferences({
      customColors: newColors
    });
  };

  const handleToggleMode = () => {
    updatePreferences({ mode: mode === 'light' ? 'dark' : 'light' });
  };

  const handleToggleDailyQuote = () => {
    setShowDailyQuote(!showDailyQuote);
    localStorage.setItem('showDailyQuote', JSON.stringify(!showDailyQuote));
  };

  const handleAddHabit = async (newHabit) => {
    if (currentUser?.uid) {
      try {
        // Verifica se o usuário está no plano básico e já tem 3 hábitos
        if (currentPlan === PLANS.BASIC && habits.length >= 3) {
          // Retorna um erro que será tratado pelo componente que chama esta função
          throw new Error('BASIC_PLAN_LIMIT');
        }

        const updatedHabits = [...habits, newHabit];
        setHabits(updatedHabits);
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { habits: updatedHabits });
      } catch (error) {
        if (error.message === 'BASIC_PLAN_LIMIT') {
          throw error; // Repassa o erro para ser tratado pelo componente
        }
        console.error('Erro ao adicionar hábito:', error);
      }
    }
  };

  const handleDeleteHabit = async (habitId) => {
    if (currentUser?.uid) {
      try {
        const updatedHabits = habits.filter(h => h.id !== habitId);
        setHabits(updatedHabits);
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { habits: updatedHabits });
      } catch (error) {
        console.error('Erro ao excluir hábito:', error);
      }
    }
  };

  const handleUpdateHabit = async (habitId, updates) => {
    if (currentUser?.uid) {
      try {
        const updatedHabits = habits.map(h => h.id === habitId ? { ...h, ...updates } : h);
        setHabits(updatedHabits);
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { habits: updatedHabits });
      } catch (error) {
        console.error('Erro ao atualizar hábito:', error);
      }
    }
  };

  const handleToggleHabit = async (habitId, dateStr) => {
    if (currentUser?.uid) {
      try {
        const habitToUpdate = habits.find(h => h.id === habitId);
        if (!habitToUpdate) return;

        const isCompleted = habitToUpdate.completedDates?.includes(dateStr);
        let updatedCompletedDates;

        if (isCompleted) {
          // Remove a data se já estiver completo
          updatedCompletedDates = habitToUpdate.completedDates.filter(date => date !== dateStr);
        } else {
          // Adiciona a data se não estiver completo
          updatedCompletedDates = [...(habitToUpdate.completedDates || []), dateStr];
        }

        const updatedHabit = {
          ...habitToUpdate,
          completedDates: updatedCompletedDates
        };

        const updatedHabits = habits.map(h => h.id === habitId ? updatedHabit : h);
        setHabits(updatedHabits);

        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { habits: updatedHabits });
      } catch (error) {
        console.error('Erro ao alternar hábito:', error);
      }
    }
  };

  const handleDeleteAllHabits = async () => {
    if (currentUser?.uid) {
      try {
        setHabits([]);
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { habits: [] });
      } catch (error) {
        console.error('Erro ao excluir todos os hábitos:', error);
      }
    }
  };

  const handleReorderHabits = async (startIndex, endIndex) => {
    if (currentUser?.uid) {
      try {
        const items = Array.from(habits);
        const [reorderedItem] = items.splice(startIndex, 1);
        items.splice(endIndex, 0, reorderedItem);
        
        const updatedHabits = items.map((habit, index) => ({
          ...habit,
          order: index
        }));
        
        setHabits(updatedHabits);
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { habits: updatedHabits });
      } catch (error) {
        console.error('Erro ao reordenar hábitos:', error);
      }
    }
  };

  React.useEffect(() => {
    const savedShowDailyQuote = localStorage.getItem('showDailyQuote');
    if (savedShowDailyQuote !== null) {
      setShowDailyQuote(JSON.parse(savedShowDailyQuote));
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PlayerProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={
            <PrivateRoute>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Navigation
                  mode={mode}
                  onToggleMode={handleToggleMode}
                  customColors={customColors}
                  onUpdateCustomColors={handleUpdateCustomColors}
                  showDailyQuote={showDailyQuote}
                  onToggleDailyQuote={handleToggleDailyQuote}
                  currentTab={currentTab}
                  setCurrentTab={setCurrentTab}
                  habits={habits}
                  onAddHabit={handleAddHabit}
                  onDeleteHabit={handleDeleteHabit}
                  onUpdateHabit={handleUpdateHabit}
                  onToggleHabit={handleToggleHabit}
                  onDeleteAllHabits={handleDeleteAllHabits}
                  onReorderHabits={handleReorderHabits}
                  userId={currentUser?.uid}
                />
              </Box>
            </PrivateRoute>
          } />
          <Route 
            path="/events" 
            element={
              <PrivateRoute>
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <Navigation
                    mode={mode}
                    onToggleMode={handleToggleMode}
                    customColors={customColors}
                    onUpdateCustomColors={handleUpdateCustomColors}
                    showDailyQuote={showDailyQuote}
                    onToggleDailyQuote={handleToggleDailyQuote}
                    currentTab="events"
                    setCurrentTab={setCurrentTab}
                    habits={habits}
                    onAddHabit={handleAddHabit}
                    onDeleteHabit={handleDeleteHabit}
                    onUpdateHabit={handleUpdateHabit}
                    onToggleHabit={handleToggleHabit}
                    onDeleteAllHabits={handleDeleteAllHabits}
                    onReorderHabits={handleReorderHabits}
                    userId={currentUser?.uid}
                  >
                    <Events customColors={customColors} />
                  </Navigation>
                </Box>
              </PrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <MiniPlayer customColors={customColors} />
      </PlayerProvider>
    </ThemeProvider>
  );
}

// Componente principal que fornece os contextos
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PlanProvider>
          <UserPreferencesProvider>
            <AppContent />
          </UserPreferencesProvider>
        </PlanProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;